import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
    modulesService,
    type ModuleCode,
    type Plan,
    type PlanImpact,
    type PlanInput,
    type PlanModuleInfo,
} from '@/services/modulesService'

const inputCls =
    'rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500'

const clp = (n: number) => (n === 0 ? 'Gratis / cotización' : `$${n.toLocaleString('es-CL')}`)
const limit = (n: number) => (n === -1 ? 'ilimitado' : n.toLocaleString('es-CL'))

function errorMessage(e: unknown, fallback: string): string {
    const err = e as { response?: { data?: { error?: string } } }
    return err?.response?.data?.error ?? fallback
}

type Form = Required<Omit<PlanInput, 'description'>> & { description: string }

const EMPTY: Form = {
    code: '', name: '', description: '', priceClp: 0, maxAgencies: 3, maxUsers: 10,
    maxOperationsPerMonth: 100, active: true, modules: [],
}

/** Back office: planes comerciales y los módulos que incluye cada uno. */
export default function PlatformPlansPage() {
    const [plans, setPlans] = useState<Plan[]>([])
    const [catalog, setCatalog] = useState<PlanModuleInfo[]>([])
    const [editing, setEditing] = useState<Plan | 'new' | null>(null)
    const [form, setForm] = useState<Form>(EMPTY)
    const [impact, setImpact] = useState<PlanImpact | null>(null)
    const [keepForExisting, setKeepForExisting] = useState(true)
    const [saving, setSaving] = useState(false)

    const load = useCallback(async () => {
        try {
            const [p, c] = await Promise.all([modulesService.plans(), modulesService.catalog()])
            setPlans(p)
            setCatalog(c)
        } catch {
            toast.error('No se pudieron cargar los planes')
        }
    }, [])

    useEffect(() => { load() }, [load])

    const label = (code: ModuleCode) => catalog.find((m) => m.code === code)?.label ?? code

    const openNew = () => { setForm(EMPTY); setEditing('new'); setImpact(null) }
    const openEdit = (p: Plan) => {
        setForm({
            code: p.code, name: p.name, description: p.description ?? '', priceClp: p.priceClp,
            maxAgencies: p.maxAgencies, maxUsers: p.maxUsers, maxOperationsPerMonth: p.maxOperationsPerMonth,
            active: p.active, modules: [...p.modules],
        })
        setEditing(p)
        setImpact(null)
        setKeepForExisting(true)
    }

    const toggleModule = (code: ModuleCode) =>
        setForm((f) => ({ ...f, modules: f.modules.includes(code) ? f.modules.filter((m) => m !== code) : [...f.modules, code] }))

    const save = async (e?: React.FormEvent) => {
        e?.preventDefault()
        if (!editing) return
        setSaving(true)
        try {
            if (editing === 'new') {
                await modulesService.createPlan(form)
                toast.success('Plan creado')
            } else {
                const { code: _code, ...input } = form
                // Primero simular: si se quitan módulos a un plan con clientes, pedir confirmación.
                if (!impact) {
                    const preview = await modulesService.updatePlan(editing.id, input, { dryRun: true, keepForExisting })
                    if (preview.removedModules.length > 0 && preview.affectedTenants.length > 0) {
                        setImpact(preview)
                        return
                    }
                }
                await modulesService.updatePlan(editing.id, input, { dryRun: false, keepForExisting })
                toast.success('Plan actualizado')
            }
            setEditing(null)
            setImpact(null)
            load()
        } catch (err) {
            toast.error(errorMessage(err, 'No se pudo guardar el plan'))
        } finally {
            setSaving(false)
        }
    }

    return (
        <div>
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-semibold">Planes y módulos</h1>
                    <p className="mt-1 max-w-2xl text-sm text-slate-400">
                        Cada plan incluye un conjunto de módulos. Los clientes ven y pueden usar solo lo que incluye su plan,
                        más los extras que les asignes desde <span className="text-slate-300">Tenants → Módulos</span>.
                    </p>
                </div>
                <button onClick={openNew} className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500">
                    Nuevo plan
                </button>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {plans.map((p) => (
                    <div key={p.id} className={`rounded-xl border border-slate-800 bg-slate-800/40 p-5 ${p.active ? '' : 'opacity-60'}`}>
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="text-lg font-semibold text-slate-100">
                                    {p.name} {!p.active && <span className="ml-2 text-xs uppercase text-slate-400">inactivo</span>}
                                </div>
                                <div className="font-mono text-xs text-slate-500">{p.code}</div>
                            </div>
                            <div className="text-right">
                                <div className="font-semibold text-slate-100">{clp(p.priceClp)}</div>
                                <div className="text-xs text-slate-500">{p.tenantCount} cliente{p.tenantCount === 1 ? '' : 's'}</div>
                            </div>
                        </div>
                        <div className="mt-3 text-xs text-slate-400">
                            {limit(p.maxUsers)} usuarios · {limit(p.maxAgencies)} instituciones · {limit(p.maxOperationsPerMonth)} operaciones/mes
                        </div>
                        <div className="mt-3 flex flex-wrap gap-1.5">
                            {p.modules.length === 0
                                ? <span className="text-xs text-slate-500">Solo el núcleo</span>
                                : p.modules.map((m) => (
                                    <span key={m} className="rounded-full bg-indigo-500/15 px-2 py-0.5 text-xs text-indigo-200">{label(m)}</span>
                                ))}
                        </div>
                        <button onClick={() => openEdit(p)} className="mt-4 text-sm text-indigo-400 hover:underline">Editar</button>
                    </div>
                ))}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4">
                    <form onSubmit={save} role="dialog" aria-label={editing === 'new' ? 'Nuevo plan' : `Editar ${editing.name}`}
                        className="my-8 w-full max-w-2xl rounded-xl border border-slate-700 bg-slate-900 p-6">
                        <h2 className="mb-4 text-lg font-semibold">{editing === 'new' ? 'Nuevo plan' : `Editar ${editing.name}`}</h2>

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <label className="text-xs text-slate-400">Código
                                <input value={form.code} disabled={editing !== 'new'} required placeholder="PROFESIONAL_2027"
                                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                                    className={`${inputCls} mt-1 w-full font-mono disabled:opacity-60`} />
                            </label>
                            <label className="text-xs text-slate-400">Nombre
                                <input value={form.name} required onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    className={`${inputCls} mt-1 w-full`} />
                            </label>
                            <label className="text-xs text-slate-400 sm:col-span-2">Descripción
                                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    className={`${inputCls} mt-1 w-full`} />
                            </label>
                            <label className="text-xs text-slate-400">Precio mensual (CLP)
                                <input type="number" min={0} value={form.priceClp} onChange={(e) => setForm({ ...form, priceClp: Number(e.target.value) })}
                                    className={`${inputCls} mt-1 w-full`} />
                            </label>
                            <label className="text-xs text-slate-400">Usuarios (-1 = ilimitado)
                                <input type="number" min={-1} value={form.maxUsers} onChange={(e) => setForm({ ...form, maxUsers: Number(e.target.value) })}
                                    className={`${inputCls} mt-1 w-full`} />
                            </label>
                            <label className="text-xs text-slate-400">Instituciones (-1 = ilimitado)
                                <input type="number" min={-1} value={form.maxAgencies} onChange={(e) => setForm({ ...form, maxAgencies: Number(e.target.value) })}
                                    className={`${inputCls} mt-1 w-full`} />
                            </label>
                            <label className="text-xs text-slate-400">Operaciones por mes (-1 = ilimitado)
                                <input type="number" min={-1} value={form.maxOperationsPerMonth}
                                    onChange={(e) => setForm({ ...form, maxOperationsPerMonth: Number(e.target.value) })}
                                    className={`${inputCls} mt-1 w-full`} />
                            </label>
                            <label className="flex items-center gap-2 text-sm text-slate-300 sm:col-span-2">
                                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                                Plan activo (disponible para clientes nuevos)
                            </label>
                        </div>

                        <h3 className="mb-2 mt-5 text-sm font-semibold text-slate-300">Módulos incluidos</h3>
                        <p className="mb-3 text-xs text-slate-500">
                            El núcleo (operaciones, mapa, nudos críticos, reportes, usuarios) va siempre incluido.
                        </p>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {catalog.map((m) => (
                                <label key={m.code} className="flex cursor-pointer gap-2 rounded-lg border border-slate-800 p-2 hover:bg-slate-800/60">
                                    <input type="checkbox" className="mt-1" checked={form.modules.includes(m.code)}
                                        onChange={() => toggleModule(m.code)} aria-label={m.label} />
                                    <span>
                                        <span className="block text-sm text-slate-100">{m.label}</span>
                                        <span className="block text-xs text-slate-500">{m.description}</span>
                                    </span>
                                </label>
                            ))}
                        </div>

                        {impact && (
                            <div role="alert" className="mt-5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-100">
                                <p className="font-medium">
                                    Vas a quitar {impact.removedModules.map(label).join(', ')} de un plan con{' '}
                                    {impact.affectedTenants.length} cliente{impact.affectedTenants.length === 1 ? '' : 's'}:{' '}
                                    {impact.affectedTenants.map((t) => t.name).join(', ')}.
                                </p>
                                <label className="mt-3 flex items-start gap-2">
                                    <input type="checkbox" className="mt-1" checked={keepForExisting}
                                        onChange={(e) => setKeepForExisting(e.target.checked)} />
                                    <span>
                                        Mantenerles esos módulos como extra (recomendado). Si lo desmarcas, los clientes actuales
                                        pierden el acceso al guardar.
                                    </span>
                                </label>
                            </div>
                        )}

                        <div className="mt-6 flex justify-end gap-3">
                            <button type="button" onClick={() => { setEditing(null); setImpact(null) }}
                                className="rounded-md border border-slate-700 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800">Cancelar</button>
                            <button type="submit" disabled={saving}
                                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-60">
                                {impact ? 'Confirmar cambios' : 'Guardar'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    )
}
